import { FC, useMemo } from "react";

import {
  NOTIFICATION_ACTION_TYPES,
  createNotificationAction,
} from "shared/ui/notifications/notification-generic/base-parts/actions";
import { NOTIFICATION_TYPES } from "shared/lib/constants/notifications";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";

import { NotificationBase } from "@/modules/notifications/notification/base";
import {
  IncentiveNotificationI,
  NotificationI,
} from "@/lib/types/notification";
import Router from "@/helpers/router";
import {
  formatCents,
  formatPeriod,
  ordinal,
} from "@/modules/rewards/lib/format";

type NotificationIncentivePropsI = NotificationI & PropsWithClassNameI;

/** The bell in one sentence (C-16 S1). */
export const describeIncentiveNotification = (
  type: NotificationI["notification_type"],
  incentive: IncentiveNotificationI | undefined
): string => {
  const name = incentive?.incentive_name?.trim() || "A contest";

  if (type === NOTIFICATION_TYPES.INCENTIVE_STARTED) {
    return `${name} has started. See how it works and where you stand.`;
  }

  const amount = formatCents(incentive?.amount_cents ?? 0);
  const period =
    incentive?.period_start && incentive?.period_end
      ? ` (${formatPeriod(incentive.period_start, incentive.period_end)})`
      : "";

  return incentive?.rank
    ? `You placed ${ordinal(incentive.rank)} in ${name}${period} and won ${amount}.`
    : `You won ${amount} in ${name}${period}.`;
};

/**
 * IncentiveStarted and IncentiveWon (C-16 S1, GLE-8408): a contest the
 * caller can earn started, or a period ended with an amount for them. Opens
 * the contest on Rewards (a win on its period).
 */
export const NotificationIncentive: FC<NotificationIncentivePropsI> = (
  props
) => {
  const { id, is_seen, created_at, className, notification_type, incentive } =
    props;
  const text = useMemo(
    () => describeIncentiveNotification(notification_type, incentive),
    [notification_type, incentive]
  );
  const isWon = notification_type === NOTIFICATION_TYPES.INCENTIVE_WON;

  return (
    <NotificationBase
      id={id}
      isUnread={!is_seen}
      className={className}
      avatar={props}
      description={{ text: <>{text}</>, date: created_at }}
      actions={[
        createNotificationAction(
          NOTIFICATION_ACTION_TYPES.PRIMARY,
          isWon ? "See standings" : "Open Rewards",
          () => {
            window.location.href = incentive?.incentive_id
              ? Router.reward(
                  incentive.incentive_id,
                  isWon ? incentive.period_start : undefined
                )
              : Router.rewards();
          }
        ),
      ]}
    />
  );
};
